import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Timeline,
  Tag,
  Divider,
  Alert,
  Row,
  Col,
  Card,
  Steps
} from 'antd';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  UserOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import api from '../../../axiosConfig';
import moment from 'moment';

const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;

const SeguimientoNovedadModal = ({ visible, onClose, onUpdated, novedad }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (visible && novedad) {
      form.setFieldsValue({
        tipo_novedad: novedad.tipo_novedad,
        descripcion: novedad.descripcion,
        estado: novedad.estado,
        resolucion: novedad.resolucion || ''
      });
    }
  }, [visible, novedad, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await api.put(`/novedades-control-servicio/${novedad.id}`, values);
      message.success('Novedad actualizada correctamente');
      onUpdated();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al actualizar novedad';
      message.error(errorMsg);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const tipoNovedadLabels = {
    documentacion_incompleta: { label: 'Documentación Incompleta', emoji: '📄', color: 'orange' },
    datos_incorrectos: { label: 'Datos Incorrectos', emoji: '❌', color: 'red' },
    firma_ilegible: { label: 'Firma Ilegible', emoji: '✍️', color: 'purple' },
    firma_ilegible_paciente: { label: 'Firma Ilegible del Paciente (OTP)', emoji: '✍️', color: 'magenta' },
    otro: { label: 'Otro', emoji: '⚠️', color: 'default' }
  };

  // La novedad de firma del paciente solo se resuelve al cerrar el traslado con OTP (o se cierra al cancelarlo)
  const esperaOtp = novedad?.tipo_novedad === 'firma_ilegible_paciente' &&
    ['pendiente', 'en_revision'].includes(novedad?.estado);

  const estadoSteps = ['pendiente', 'en_revision', 'resuelta', 'cerrada'];
  const estadoLabels = {
    pendiente: 'Pendiente',
    en_revision: 'En Revisión',
    resuelta: 'Resuelta',
    cerrada: 'Cerrada'
  };

  const getCurrentStepIndex = () => {
    return estadoSteps.indexOf(novedad?.estado || 'pendiente');
  };

  if (!novedad) return null;

  const tipoInfo = tipoNovedadLabels[novedad.tipo_novedad];

  return (
    <Modal
      title={
        <Space>
          <SyncOutlined style={{ color: '#1890ff' }} />
          <span>Seguimiento de Novedad</span>
          <Tag color={tipoInfo.color}>
            {tipoInfo.emoji} {tipoInfo.label}
          </Tag>
        </Space>
      }
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      width={800}
      confirmLoading={loading}
      okText="Guardar Cambios"
      cancelText="Cerrar"
      okButtonProps={{ icon: <CheckCircleOutlined /> }}
      cancelButtonProps={{ icon: <CloseCircleOutlined /> }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Información del reporte original */}
        <Card size="small" title="Información Original" style={{ backgroundColor: '#f5f5f5' }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Space>
                <UserOutlined style={{ color: '#1890ff' }} />
                <strong>Reportado por:</strong>
                <span>
                  {novedad.usuario
                    ? `${novedad.usuario.nombre || ''}${novedad.usuario.apellido ? ' ' + novedad.usuario.apellido : ''}`.trim()
                    : 'Sistema'
                  }
                </span>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <CalendarOutlined style={{ color: '#1890ff' }} />
                <strong>Fecha:</strong>
                <span>{moment(novedad.createdAt).format('DD/MM/YYYY HH:mm')}</span>
              </Space>
            </Col>
            <Col span={24}>
              <div style={{ marginTop: 8 }}>
                <strong>Descripción del Problema:</strong>
                <div style={{
                  marginTop: 4,
                  padding: '8px 12px',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  border: '1px solid #d9d9d9'
                }}>
                  {novedad.descripcion}
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Progreso del estado */}
        <div>
          <strong style={{ fontSize: '14px', marginBottom: 8, display: 'block' }}>Progreso de Resolución:</strong>
          <Steps current={getCurrentStepIndex()} size="small">
            <Step title="Pendiente" icon={<ClockCircleOutlined />} />
            <Step title="En Revisión" icon={<SyncOutlined />} />
            <Step title="Resuelta" icon={<CheckCircleOutlined />} />
            <Step title="Cerrada" icon={<CloseCircleOutlined />} />
          </Steps>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* Formulario de seguimiento */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="estado"
            label="Cambiar Estado"
            rules={[{ required: true, message: 'Seleccione el estado' }]}
            extra={novedad.estado === 'cerrada'
              ? 'Esta novedad está cerrada'
              : esperaOtp
                ? 'Se resuelve automáticamente al cerrar el traslado con OTP desde Coordinación o Centro de Control'
                : 'Seleccione el nuevo estado de la novedad'}
          >
            <Select size="large" disabled={novedad.estado === 'cerrada' || esperaOtp}>
              <Option value="pendiente">
                <Space>
                  <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                  <span>Pendiente</span>
                </Space>
              </Option>
              <Option value="en_revision">
                <Space>
                  <SyncOutlined style={{ color: '#1890ff' }} />
                  <span>En Revisión</span>
                </Space>
              </Option>
              <Option value="resuelta">
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>Resuelta</span>
                </Space>
              </Option>
              <Option value="cerrada">
                <Space>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <span>Cerrada</span>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="resolucion"
            label="Acciones Tomadas / Resolución"
            rules={
              form.getFieldValue('estado') === 'resuelta' || form.getFieldValue('estado') === 'cerrada'
                ? [{ required: true, message: 'Debe describir cómo se resolvió la novedad' }]
                : []
            }
            extra="Describa las acciones tomadas para resolver esta novedad"
          >
            <TextArea
              rows={5}
              maxLength={1000}
              showCount
              placeholder="Ejemplo: Se contactó al paciente vía telefónica, el paciente asistió el día 15/12/2025 y firmó nuevamente el documento de manera legible. Documento actualizado adjunto en el sistema."
              style={{ fontSize: '14px' }}
            />
          </Form.Item>

          {novedad.resolucion && (
            <Alert
              message="Resolución Anterior"
              description={novedad.resolucion}
              type="info"
              showIcon
            />
          )}
        </Form>

        {/* Timeline de historial completo */}
        {novedad.historial && novedad.historial.length > 0 && (
          <Card size="small" title="Historial de Cambios">
            <Timeline>
              {novedad.historial.map((registro, index) => {
                const accionIcons = {
                  creacion: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                  cambio_estado: <SyncOutlined style={{ color: '#1890ff' }} />,
                  actualizacion_tipo: <SyncOutlined style={{ color: '#722ed1' }} />,
                  actualizacion_descripcion: <SyncOutlined style={{ color: '#fa8c16' }} />,
                  actualizacion_resolucion: <CheckCircleOutlined style={{ color: '#52c41a' }} />
                };

                const accionLabels = {
                  creacion: 'Creación',
                  cambio_estado: 'Cambio de Estado',
                  actualizacion_tipo: 'Actualización de Tipo',
                  actualizacion_descripcion: 'Actualización de Descripción',
                  actualizacion_resolucion: 'Actualización de Resolución'
                };

                const accionColors = {
                  creacion: 'green',
                  cambio_estado: 'blue',
                  actualizacion_tipo: 'purple',
                  actualizacion_descripcion: 'orange',
                  actualizacion_resolucion: 'green'
                };

                return (
                  <Timeline.Item
                    key={registro.id}
                    color={accionColors[registro.accion] || 'gray'}
                    dot={accionIcons[registro.accion]}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <Space>
                        <Tag color={accionColors[registro.accion]}>
                          {accionLabels[registro.accion] || registro.accion}
                        </Tag>
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                          {moment(registro.created_at).format('DD/MM/YYYY HH:mm')}
                        </span>
                      </Space>
                    </div>

                    {registro.descripcion && (
                      <div style={{ marginBottom: 4, color: '#595959' }}>
                        {registro.descripcion}
                      </div>
                    )}

                    {registro.accion === 'cambio_estado' && (
                      <div style={{ marginTop: 4 }}>
                        <Space size="small">
                          <Tag color="default">{estadoLabels[registro.estado_anterior]}</Tag>
                          <span>→</span>
                          <Tag color="processing">{estadoLabels[registro.estado_nuevo]}</Tag>
                        </Space>
                      </div>
                    )}

                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: 4 }}>
                      <UserOutlined style={{ marginRight: 4 }} />
                      {registro.usuario
                        ? `${registro.usuario.nombre || ''}${registro.usuario.apellido ? ' ' + registro.usuario.apellido : ''}`.trim()
                        : 'Sistema'}
                    </div>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </Card>
        )}
      </Space>
    </Modal>
  );
};

export default SeguimientoNovedadModal;
